import { useEffect, useRef, useState, type RefObject } from "react"

export type ScrollRevealVariant = "fadeUp" | "fadeIn" | "scaleIn" | "none"

const DEFAULT_THRESHOLD = 0.12

/**
 * IntersectionObserver never fires when the required ratio cannot fit in the
 * root. A 6500px about-body with threshold 0.12 needs 780px visible; a laptop
 * viewport minus the default -8% rootMargin is ~736px, so the section stays
 * opacity-0 forever (heading reveals, body does not).
 */
export function effectiveScrollRevealThreshold(
  elementHeight: number,
  rootHeight: number,
  requestedThreshold: number
): number {
  if (requestedThreshold <= 0) return 0
  if (elementHeight <= 0 || rootHeight <= 0) return requestedThreshold
  // Taller than the viewport: any fractional threshold can miss once rootMargin
  // shrinks the root. Reveal on the first intersecting pixel.
  if (elementHeight > rootHeight) return 0

  return requestedThreshold
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

type UseScrollRevealOptions = {
  threshold?: number
  disabled?: boolean
  once?: boolean
  /** IntersectionObserver rootMargin (e.g. trigger before fully in view). */
  rootMargin?: string
}

/**
 * Observes an element and returns true once it enters the viewport (or immediately when disabled).
 */
const DEFAULT_ROOT_MARGIN = "0px 0px -8% 0px"

export function useScrollReveal({
  threshold = DEFAULT_THRESHOLD,
  disabled = false,
  once = true,
  rootMargin = DEFAULT_ROOT_MARGIN
}: UseScrollRevealOptions = {}): [boolean, RefObject<HTMLDivElement | null>] {
  const [isVisible, setIsVisible] = useState(disabled || prefersReducedMotion())
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (disabled || prefersReducedMotion()) {
      setIsVisible(true)
      return undefined
    }

    const node = elementRef.current
    if (!node) return undefined

    const resolvedThreshold = effectiveScrollRevealThreshold(
      node.getBoundingClientRect().height,
      window.innerHeight,
      threshold
    )

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) return
        setIsVisible(true)
        if (once) observer.disconnect()
      },
      { threshold: resolvedThreshold, rootMargin }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [threshold, disabled, once, rootMargin])

  return [isVisible, elementRef]
}
